/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { i18nWrapper } from 'client/lib/utils';
import * as keyset from 'i18n/DraftControlPanel';
import block from 'propmods';

import { cancelSkillReview, devicesTestingApi, requestSkillDeploy, requestSkillReview } from '../../api';
import { CurrentAliceAppContext } from '../../context/aliceAppContext';
import { Context as SnapshotContext } from '../../context/snapshotContext';
import { FormErrors } from '../../hocs/types';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { useInterval } from '../../hooks/useInterval';
import { InfoIcon } from '../../icons/Info';
import { draftStatusHint } from '../../locale/ru/draftStatus';
import { skillDescriptions, skillDescriptionsAired } from '../../locale/ru/skillDescriptions';
import { smartHomeTestingTitleText } from '../../locale/ru/smartHomeTesting';
import { DevicesTestingStatusMeta } from '../../model/devicesTestingForm';
import { DraftStatus, SmartHomeValidationStatuses } from '../../model/draft';
import { Operation, OperationType } from '../../model/operation';
import { SkillAccess } from '../../model/settings';
import { AliceApp, Channel, GenericSkill } from '../../model/skill';
import { getBaseTooltipReducer } from '../../reducers';
import { AliceSkillFormSettings, BaseTooltipAction, SmartHomeFormSettings } from '../../types';
import { getCanEditApp, isPrivateSkillsEnabled } from '../../utils';
import { createOperation } from '../../utils/operations';
import { isPrivateThereminvoxSkill } from '../../utils/thereminvox';
import { ActionButton } from '../ActionButton/ActionButton';
import { Handles as ConfirmHandles, UnmanagedConfirm } from '../Confirm/UnmanagedConfirm';
import Link from '../Link/Link';
import { Loading } from '../Loading/Loading';
import { ModerationPipelineItem, ModerationPipelineItemState } from '../ModerationPipelineItem/ModerationPipelineItem';
import ReviewChecklist, { SmartHomeReviewChecklist } from '../ReviewChecklist/ReviewChecklist';
import { labels } from '../Settings/channels/labels';
import { TestingDateSelectionSection } from '../TestingDateSelection/TestingDateSelectionSection/TestingDateSelectionSection';

import { ValidationStatuses } from './ValidationStatuses';

import './DraftControlPanel.scss';

type DraftActionType = 'requestReview' | 'cancelReview' | 'requestDeploy';
type ValidationErrors<T extends Channel> = T extends Channel.AliceSkill
    ? FormErrors<AliceSkillFormSettings>
    : T extends Channel.SmartHome
      ? FormErrors<SmartHomeFormSettings>
      : never;

const b = block('DraftControlPanel');
const t = i18nWrapper(keyset);

function getDescription<TChannel extends Channel>(onAir: boolean, status: DraftStatus, channel: TChannel): string {
    if (onAir) {
        return skillDescriptionsAired[channel][status];
    }
    return skillDescriptions[channel][status];
}

export type ActionButtonTheme = 'normal' | 'action';

interface ActionButton {
    theme: ActionButtonTheme;
    action: DraftActionType;
    getText: (_isDraftCancelled: boolean) => string;
    settingsShouldBeValid: boolean;
    invalidSettingsMessage: string;
}

const buttons: Record<DraftActionType, ActionButton> = {
    requestReview: {
        theme: 'action',
        action: 'requestReview',
        getText: (isDraftCancelled: boolean) =>
            isDraftCancelled ? t('action-request-review-again') : t('action-request-review'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: '',
    },
    cancelReview: {
        theme: 'normal',
        action: 'cancelReview',
        getText: () => t('action-cancel-review'),
        settingsShouldBeValid: false,
        invalidSettingsMessage: '',
    },
    requestDeploy: {
        theme: 'action',
        action: 'requestDeploy',
        getText: () => t('action-deploy'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: t('invalid-settings'),
    },
};

const commonModerationStatuses = [
    DraftStatus.InDevelopment,
    DraftStatus.ReviewRequested,
    DraftStatus.ReviewApproved,
    DraftStatus.DeployRequested,
];

const skillSelfModerationStatuses = [DraftStatus.InDevelopment, DraftStatus.DeployRequested];
const smartHomeSelfModerationStatuses = [DraftStatus.InDevelopment];

const smartHomeModerationStatuses = [
    DraftStatus.InDevelopment,
    DraftStatus.ReviewRequested,
    DraftStatus.MeetingApproval,
    DraftStatus.WaitingForTesting,
    DraftStatus.DevicesTesting,
    DraftStatus.DevicesTestingCompleted,
    DraftStatus.ReviewApproved,
];

const commonOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatus.ReviewRequested,
    [OperationType.ReviewApproved]: DraftStatus.ReviewApproved,
    [OperationType.DeployRequested]: DraftStatus.DeployRequested,
};

const smartHomeOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatus.ReviewRequested,
    [OperationType.SettingsReviewApproved]: DraftStatus.MeetingApproval,
    [OperationType.DeviceTestPrepared]: DraftStatus.WaitingForTesting,
    [OperationType.DeviceTestStarted]: DraftStatus.DevicesTesting,
    [OperationType.DeviceTestCompleted]: DraftStatus.DevicesTestingCompleted,
    [OperationType.ReviewApproved]: DraftStatus.DevicesTestingCompleted,
    [OperationType.DeployRequested]: DraftStatus.DeployRequested,
};

const operationTypeDraftStatusMaps: Record<Channel, Record<string, string>> = {
    [Channel.AliceSkill]: commonOperationTypeDraftStatusMap,
    [Channel.Thereminvox]: commonOperationTypeDraftStatusMap,
    [Channel.SmartHome]: smartHomeOperationTypeDraftStatusMap,
    [Channel.SmartScreenApp]: commonOperationTypeDraftStatusMap,
    [Channel.AutoApp]: commonOperationTypeDraftStatusMap,
    [Channel.TvApp]: commonOperationTypeDraftStatusMap,
};

export const getModerationStatuses = (aliceApp: AliceApp) => {
    if (aliceApp.canSkipReview) {
        return aliceApp.draft.channel === Channel.AliceSkill
            ? skillSelfModerationStatuses
            : smartHomeSelfModerationStatuses;
    }

    return aliceApp.draft.channel === Channel.SmartHome ? smartHomeModerationStatuses : commonModerationStatuses;
};

const DEFAULT_STATUS_TO_ACTION: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [],
    [DraftStatus.ReviewRequested]: [],
    [DraftStatus.ReviewApproved]: [],
    [DraftStatus.DeployRequested]: [],
    [DraftStatus.ReviewCancelled]: [],
    [DraftStatus.ReviewRejected]: [],
    [DraftStatus.DeployRejected]: [],
    [DraftStatus.TestRequired]: [],
    [DraftStatus.MeetingApproval]: [],
    [DraftStatus.WaitingForTesting]: [],
    [DraftStatus.AppScanRequested]: [],
    [DraftStatus.AppScanRequestedAfterModeration]: [],
    [DraftStatus.DevicesTesting]: [],
    [DraftStatus.DevicesTestingCompleted]: [],
};

const commonStatusToActions: Record<DraftStatus, ActionButton[]> = {
    ...DEFAULT_STATUS_TO_ACTION,
    [DraftStatus.InDevelopment]: [buttons.requestReview],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
};

const selfModerationStatusToActions: Record<DraftStatus, ActionButton[]> = {
    ...DEFAULT_STATUS_TO_ACTION,
    [DraftStatus.InDevelopment]: [buttons.requestDeploy],
};

const smartHomeStatusToActions: Record<DraftStatus, ActionButton[]> = {
    ...DEFAULT_STATUS_TO_ACTION,
    [DraftStatus.InDevelopment]: [buttons.requestReview],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.MeetingApproval]: [buttons.cancelReview],
    [DraftStatus.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
};

const smartHomeselfmoderationStatusToActions: Record<DraftStatus, ActionButton[]> = {
    ...DEFAULT_STATUS_TO_ACTION,
    [DraftStatus.InDevelopment]: [buttons.requestDeploy],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.MeetingApproval]: [buttons.cancelReview],
    [DraftStatus.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
};

const statusToActions: Record<Channel, Record<DraftStatus, ActionButton[]>> = {
    [Channel.AliceSkill]: commonStatusToActions,
    [Channel.Thereminvox]: commonStatusToActions,
    [Channel.SmartHome]: smartHomeStatusToActions,
    [Channel.TvApp]: commonStatusToActions,
    [Channel.SmartScreenApp]: commonStatusToActions,
    [Channel.AutoApp]: commonStatusToActions,
};

const selfModerationChannelToActions = {
    [Channel.AliceSkill]: selfModerationStatusToActions,
    [Channel.AutoApp]: selfModerationStatusToActions,
    [Channel.Thereminvox]: commonStatusToActions,
    [Channel.SmartHome]: smartHomeselfmoderationStatusToActions,
    [Channel.SmartScreenApp]: selfModerationStatusToActions,
    [Channel.TvApp]: selfModerationStatusToActions,
};

export function getActionButtons<TChannel extends Channel>(skill: GenericSkill<TChannel, any, any>): ActionButton[] {
    const channel = skill.channel;
    const status = skill.draft.status;

    if (skill.canSkipReview) {
        return selfModerationChannelToActions?.[channel]?.[status as DraftStatus] || [];
    }

    return statusToActions[channel][status] || [];
}

interface Props {
    settingsAreValid: boolean;
    safeToRequestReview?: boolean;
    afterReviewRequest?: () => void;
}

export const splitModerationStatusesByCurrentStatus = (moderationStatuses: Array<DraftStatus>, status: DraftStatus) => {
    const currentStatusIndex = moderationStatuses.findIndex((s: string) => s === status);

    const moderationStatusesBeforeCurrent = moderationStatuses.slice(0, currentStatusIndex);
    const moderationStatusesAfterCurrent = moderationStatuses.slice(currentStatusIndex + 1);

    return {
        moderationStatusesBeforeCurrent,
        moderationStatusesAfterCurrent,
    };
};

export const checkIfCancelled = (operations: Operation[], channel: Channel) => {
    const defaultStatus = DraftStatus.InDevelopment;
    const lastOperation = operations[0];
    const secondLastOperation = operations[1];

    const isCancelled = lastOperation.type === OperationType.ReviewCancelled;

    const cancelledOnStatus: DraftStatus = isCancelled
        ? (operationTypeDraftStatusMaps as any)[channel][secondLastOperation.type] ?? defaultStatus
        : defaultStatus;

    return isCancelled ? { cancelledOnStatus, cancellationComment: lastOperation.comment } : null;
};

const unsavedAlertMessage = t('unsaved-alert');

type SkillActions = {
    error: string;
    hasPendingAction: boolean;
    statuses?: SmartHomeValidationStatuses;
};

export const useSkillActionsWrapper = () => {
    const { refreshSnapshot } = useContext(SnapshotContext);
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [{ error, hasPendingAction, statuses }, setState] = useState<SkillActions>({
        error: '',
        hasPendingAction: false,
    });

    interface WrapHooks {
        onDone?: () => void;
    }

    const wrap = useCallback(
        function <T>(action: (_id: string) => Promise<T | void>, hooks?: WrapHooks) {
            return async () => {
                const { onDone } = hooks ?? {};
                setState({ hasPendingAction: true, error: '' });

                try {
                    const result = await action(aliceApp.id);
                    await refreshSnapshot();
                    setState({ hasPendingAction: false, error: '' });
                    onDone && onDone();

                    return result;
                } catch (e) {
                    const message = makeValidationErrorMessage(aliceApp.channel, e);
                    await refreshSnapshot();
                    const inferError = e as any;
                    setState({ hasPendingAction: false, error: message, statuses: inferError?.payload?.statuses });
                }
            };
        },
        [aliceApp, refreshSnapshot],
    );

    return {
        wrapper: wrap,
        error,
        hasPendingAction,
        statuses,
    };
};

const timingsByStatus: Record<DraftStatus, '' | keyof (typeof keyset)['en']> = {
    [DraftStatus.InDevelopment]: '',
    [DraftStatus.ReviewRequested]: 'timings-1-day',
    [DraftStatus.MeetingApproval]: 'timings-2-weeks',
    [DraftStatus.WaitingForTesting]: '',
    [DraftStatus.DevicesTesting]: 'timings-30-min',
    [DraftStatus.DevicesTestingCompleted]: 'timings-2-weeks',
    [DraftStatus.ReviewApproved]: '',
    [DraftStatus.DeployRequested]: '',
    [DraftStatus.ReviewCancelled]: '',
    [DraftStatus.ReviewRejected]: '',
    [DraftStatus.DeployRejected]: '',
    [DraftStatus.TestRequired]: '',
    [DraftStatus.AppScanRequested]: '',
    [DraftStatus.AppScanRequestedAfterModeration]: '',
};

const extendedTimingsTextByStatus: Record<DraftStatus, '' | keyof (typeof keyset)['en']> = {
    [DraftStatus.InDevelopment]: '',
    [DraftStatus.ReviewRequested]: '',
    [DraftStatus.MeetingApproval]: 'timings-sh-meeting',
    [DraftStatus.WaitingForTesting]: '',
    [DraftStatus.DevicesTesting]: '',
    [DraftStatus.DevicesTestingCompleted]: '',
    [DraftStatus.ReviewApproved]: '',
    [DraftStatus.DeployRequested]: '',
    [DraftStatus.ReviewCancelled]: '',
    [DraftStatus.ReviewRejected]: '',
    [DraftStatus.DeployRejected]: '',
    [DraftStatus.TestRequired]: '',
    [DraftStatus.AppScanRequested]: '',
    [DraftStatus.AppScanRequestedAfterModeration]: '',
};

export const getSlaTimings = <T extends Channel>(
    state: ModerationPipelineItemState,
    channel: T,
    draftStatus: DraftStatus,
) => {
    if (channel === Channel.SmartHome) {
        const timingsText = t(timingsByStatus[draftStatus]);
        const extendedTimingsText = t(extendedTimingsTextByStatus[draftStatus]);

        switch (state) {
            case 'not-started':
                return timingsText;
            case 'current':
                return extendedTimingsText || (timingsText ? `${t('timings-text')} ${timingsText}` : undefined);
            default:
                return undefined;
        }
    }
};

export const DraftControlPanel: React.FC<Props> = ({ settingsAreValid, safeToRequestReview, afterReviewRequest }) => {
    const confirmRef = useRef<ConfirmHandles>(null);
    const { aliceApp, operations, addOperation } = useContext(CurrentAliceAppContext)!;
    const { wrapper, error, hasPendingAction, statuses } = useSkillActionsWrapper();
    const [devicesTestingStatusMeta, setDevicesTestingStatusMeta] = useState<DevicesTestingStatusMeta>();

    const disabled = !getCanEditApp(aliceApp);

    const handler = useCallback(() => {
        return devicesTestingApi.getDevicesTestingRecordStatus(aliceApp.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp.id]);

    const [loadStatusHandler, state] = useFetchingHandler({ handler });

    const loadStatus = useCallback(async () => {
        setDevicesTestingStatusMeta(await loadStatusHandler());
    }, [loadStatusHandler]);

    const loadStatusUnhandled = useCallback(async () => {
        setDevicesTestingStatusMeta(await handler());
    }, [handler]);

    useEffect(() => {
        void loadStatus();
    }, [loadStatus]);
    useInterval(loadStatusUnhandled, 1000 * 60);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cancelReview = useCallback(
        wrapper(cancelSkillReview, {
            onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.SkillWithdrawn })),
        }),
        [addOperation, wrapper],
    );

    const requestDeploy = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(unsavedAlertMessage);

        if (!changesDiscarded) {
            return;
        }

        const requestDeployFunc = requestSkillDeploy;

        const action = wrapper(requestDeployFunc, {
            onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.DeployRequested })),
        });

        return await action();
    }, [safeToRequestReview, addOperation, aliceApp, wrapper]);

    const displaySkillAccess = isPrivateSkillsEnabled();

    const requestReview = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(unsavedAlertMessage);

        if (!changesDiscarded) {
            return;
        }

        const onConfirm = async () => {
            const requestReviewFunc = requestSkillReview;

            const action = wrapper(requestReviewFunc, {
                onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.ReviewRequested })),
            });

            afterReviewRequest && afterReviewRequest();

            await action();
        };

        if (aliceApp.channel === Channel.AliceSkill) {
            return confirmRef.current!.open({
                title: t('make-sure'),
                description: (
                    <ReviewChecklist
                        hidden={
                            (displaySkillAccess && aliceApp.draft.skillAccess === SkillAccess.Hidden) ||
                            (!displaySkillAccess && aliceApp.draft.hideInStore)
                        }
                    />
                ),
                onConfirm,
            });
        }

        if (aliceApp.channel === Channel.SmartHome) {
            return confirmRef.current!.open({
                title: t('sh-precheck'),
                description: (
                    <>
                        <p {...b('smart-home-confirm-title')}>{smartHomeTestingTitleText}</p>
                        <SmartHomeReviewChecklist />
                    </>
                ),
                width: 600,
                onConfirm,
                confirmText: t('continue'),
                rejectText: t('back'),
            });
        }

        await onConfirm();
    }, [aliceApp, addOperation, safeToRequestReview, afterReviewRequest, displaySkillAccess, wrapper]);

    if (isPrivateThereminvoxSkill(aliceApp.draft)) {
        return null;
    }

    if (!operations.length) {
        return <Loading centered size="l" />;
    }

    const actions = {
        cancelReview: async (): Promise<BaseTooltipAction> => {
            await cancelReview();
            return 'clear';
        },
        requestDeploy: async (): Promise<BaseTooltipAction> => {
            const res = await requestDeploy();

            if (!res) {
                return 'clear';
            }

            return res.published ? 'ok' : 'clear';
        },
        requestReview: async (): Promise<BaseTooltipAction> => {
            await requestReview();
            return 'clear';
        },
    };

    const { status, channel } = aliceApp.draft;

    const moderationStatuses = getModerationStatuses(aliceApp) as Array<DraftStatus>;

    const { cancellationComment, cancelledOnStatus } = checkIfCancelled(operations, channel) ?? {};
    const isCancelled = Boolean(cancelledOnStatus);

    const lastNonCancelledStatus = cancelledOnStatus ?? status;

    const { moderationStatusesBeforeCurrent, moderationStatusesAfterCurrent } = splitModerationStatusesByCurrentStatus(
        moderationStatuses,
        lastNonCancelledStatus,
    );

    const isPrivate = aliceApp.draft.skillAccess === SkillAccess.Private;

    const isMeetingApproval = channel === Channel.SmartHome && status === DraftStatus.MeetingApproval;
    const isTesting =
        channel === Channel.SmartHome &&
        (status === DraftStatus.WaitingForTesting || status === DraftStatus.DevicesTesting);
    const zoomLink =
        (isTesting &&
            devicesTestingStatusMeta &&
            devicesTestingStatusMeta.status !== 'NOT_SPECIFIED' &&
            devicesTestingStatusMeta.record.type === 'zoom' &&
            devicesTestingStatusMeta.record.options.zoomLink) ||
        null;
    const displayTestingDateSelector =
        (__appConfig.app.displayTestingDateSelector ||
            Boolean(sessionStorage.getItem('__display-testing-date-selector'))) &&
        !isPrivate;

    return (
        <div {...b()}>
            <div {...b('pipeline')}>
                {moderationStatusesBeforeCurrent.map((draftStatus, i) => (
                    <div {...b('item')} key={i}>
                        {draftStatus === DraftStatus.MeetingApproval && displayTestingDateSelector ? (
                            <ModerationPipelineItem
                                draftStatus={draftStatus}
                                channel={channel}
                                state="completed"
                                position={i + 1}
                                showSuccessText={false}
                            >
                                <TestingDateSelectionSection
                                    state={state}
                                    loadStatusUnhandled={loadStatusUnhandled}
                                    devicesTestingStatusMeta={devicesTestingStatusMeta}
                                    disabled={disabled}
                                />
                            </ModerationPipelineItem>
                        ) : (
                            <ModerationPipelineItem
                                draftStatus={draftStatus}
                                channel={channel}
                                state="completed"
                                position={i + 1}
                            />
                        )}
                    </div>
                ))}
                <div {...b('item', { current: true })}>
                    <ModerationPipelineItem
                        draftStatus={lastNonCancelledStatus}
                        state={isCancelled ? 'rejected' : 'current'}
                        channel={channel}
                        position={moderationStatusesBeforeCurrent.length + 1}
                        key={moderationStatusesBeforeCurrent.length}
                    >
                        {isMeetingApproval && displayTestingDateSelector ? (
                            <TestingDateSelectionSection
                                state={state}
                                loadStatusUnhandled={loadStatusUnhandled}
                                devicesTestingStatusMeta={devicesTestingStatusMeta}
                                disabled={disabled}
                            />
                        ) : !isCancelled ? (
                            <>
                                <div {...b('description')}>{getDescription(aliceApp.onAir, status, channel)}</div>
                                {!isPrivate && !settingsAreValid && draftStatusHint[status] ? (
                                    <div {...b('hint')}>{draftStatusHint[status]}</div>
                                ) : null}
                                {getSlaTimings('current', channel, lastNonCancelledStatus)}
                            </>
                        ) : (
                            <div {...b('description', { cancelled: true })}>
                                <div {...b('info-icon')}>
                                    <InfoIcon color="#000" type="stroke" size="xs" />
                                </div>
                                {cancellationComment}
                            </div>
                        )}
                        {zoomLink && (
                            <div {...b('zoom-link-wrapper')}>
                                <div {...b('zoom-link-hint')}>{t('meeting-zoom-link')}</div>
                                <Link to={zoomLink} target="_blank" {...b('zoom-link')}>
                                    {zoomLink}
                                </Link>
                            </div>
                        )}
                        <div {...b('buttons')}>
                            {getActionButtons(aliceApp).map((button) => {
                                const disabledInvalid = button.settingsShouldBeValid && !settingsAreValid;

                                return (
                                    <div key={button.action} {...b('button')}>
                                        <ActionButton
                                            tooltipReducer={getBaseTooltipReducer({
                                                successLabel: button.action === 'requestDeploy' ? t('published') : '',
                                            })}
                                            action={actions[button.action]}
                                            defaultTooltipState={{ tooltipVisible: false }}
                                            disabled={hasPendingAction || disabledInvalid || disabled}
                                            theme={button.theme}
                                        >
                                            {button.getText(isCancelled)}
                                        </ActionButton>
                                        {disabledInvalid && <div {...b('error')}>{button.invalidSettingsMessage}</div>}
                                    </div>
                                );
                            })}
                            {error && <div {...b('error')}>{error}</div>}
                            {statuses && <ValidationStatuses statuses={statuses} />}
                        </div>
                    </ModerationPipelineItem>
                </div>

                {moderationStatusesAfterCurrent.map((status, i) => {
                    const initialIndex = moderationStatusesBeforeCurrent.length + i + 1;

                    return (
                        <div {...b('item')} key={initialIndex}>
                            <ModerationPipelineItem
                                draftStatus={status}
                                state="not-started"
                                channel={channel}
                                position={initialIndex + 1}
                                slaTimingsText={getSlaTimings('not-started', channel, status)}
                            />
                        </div>
                    );
                })}
            </div>
            <UnmanagedConfirm ref={confirmRef} />
        </div>
    );
};

function makeValidationErrorMessage<C extends Channel>(channel: C, error?: unknown) {
    const header = t('errors-validation') + '\n';

    const l = labels[channel];

    if (typeof error === 'object' && error && 'fields' in error && !!error.fields) {
        const errorField = error?.fields || {};
        return (
            header +
            Object.entries(errorField as ValidationErrors<C>)
                .map(([k, v]) => {
                    const label = k in l ? (l as any)[k] : t('others');
                    const value = Array.isArray(v) ? v.filter(Boolean)[0] : v;

                    return `— ${label}: ${value}`;
                })
                .join('\n')
        );
    }
    return t('error-try-again');
}
